import {useEffect,useMemo,useState} from 'react';
import type {SmtAvailabilityIntent,SmtAvailabilityViewModel} from '../../../../packages/runtime/smt-availability-contract.ts';
import type {SmtAvailabilityPort} from '../smt-availability-port.ts';

type StatusFilter='all'|'available'|'soldout'|'paused';
type NodeIntentKind='availability.mark-soldout'|'availability.restore'|'availability.pause'|'availability.resume';

const statusLabel={available:'可售',soldout:'已售罄',paused:'已暫停'} as const;
const kindLabel={product:'產品',ingredient:'原料',other:'其他'} as const;
const errorText=(error:unknown)=>error instanceof Error?error.message:'AVAILABILITY_UNKNOWN_ERROR';

export function SoldOutPage({runtime}:{runtime:SmtAvailabilityPort}){
  const [snapshot,setSnapshot]=useState<SmtAvailabilityViewModel|null>(null);
  const [selected,setSelected]=useState<readonly string[]>([]);
  const [query,setQuery]=useState('');
  const [statusFilter,setStatusFilter]=useState<StatusFilter>('all');
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState<string|null>(null);

  useEffect(()=>{
    let ignore=false;
    runtime.readAvailability().then(next=>{
      if(ignore)return;
      setSnapshot(next);
      setLoading(false);
      setError(null);
    }).catch(reason=>{
      if(ignore)return;
      setLoading(false);
      setError(errorText(reason));
    });
    return()=>{ignore=true;};
  },[runtime]);

  const visibleNodes=useMemo(()=>{
    if(!snapshot)return [];
    const needle=query.trim().toLocaleLowerCase('zh-HK');
    return snapshot.nodes.filter(node=>{
      if(statusFilter!=='all'&&node.status!==statusFilter)return false;
      if(!needle)return true;
      const haystack=[node.nodeId,node.productId??'',...node.affectedProductIds].join(' ').toLocaleLowerCase('zh-HK');
      return haystack.includes(needle);
    });
  },[snapshot,query,statusFilter]);

  const selectedSet=useMemo(()=>new Set(selected),[selected]);
  const canMutate=Boolean(snapshot?.permissions.canChangeAvailability)&&selected.length>0&&!loading;

  const toggle=(nodeId:string)=>setSelected(current=>current.includes(nodeId)?current.filter(id=>id!==nodeId):[...current,nodeId]);
  const selectVisible=()=>setSelected(current=>{
    const next=new Set(current);
    for(const node of visibleNodes)next.add(node.nodeId);
    return [...next];
  });
  const clearSelection=()=>setSelected([]);

  const runIntent=async(kind:NodeIntentKind)=>{
    if(!snapshot||selected.length===0)return;
    setLoading(true);
    try{
      const intent:SmtAvailabilityIntent={kind,nodeIds:selected};
      const result=await runtime.dispatchAvailability(intent,snapshot.revision);
      setSnapshot(result.snapshot);
      if(result.kind==='accepted'){
        setSelected([]);
        setError(null);
      }else{
        setError(result.code);
      }
    }catch(reason){
      setError(errorText(reason));
    }finally{
      setLoading(false);
    }
  };

  if(!snapshot){
    return <main className="soldout-page" aria-busy={loading}><section className="soldout-empty glass-panel"><span className="eyebrow">UI-SMT-05</span><h1>售罄／供應狀態</h1><p>{loading?'讀取 C Availability…':'暫時未能讀取供應狀態。'}</p>{error?<div role="alert">{error}</div>:null}</section></main>;
  }

  const daily=snapshot.dailySoldoutRestorePolicy;
  return <main className="soldout-page" aria-busy={loading}>
    <section className="soldout-head glass-panel">
      <div><span className="eyebrow">UI-SMT-05 · C AVAILABILITY</span><h1>售罄／供應狀態</h1><p>Store {snapshot.storeId} · Rev {snapshot.revision}</p></div>
      <div className="soldout-summary" aria-label="供應狀態摘要"><strong>{snapshot.summary.soldoutNodes}</strong><span>售罄</span><strong>{snapshot.summary.pausedNodes}</strong><span>暫停</span><strong>{snapshot.summary.affectedProductsUnavailable}</strong><span>受影響產品</span></div>
    </section>

    <section className="soldout-policy glass-panel">
      <div><b>每日自動恢復</b><span>{daily.enabled?daily.restoreLocalTime?`${daily.restoreLocalTime} 自動恢復`:'已啟用':'未啟用'}</span></div>
      <small>恢復時間及執行由 C Availability／Store Policy 管理；此頁唔自行跑計時器。</small>
    </section>

    {error?<div className="soldout-error" role="alert">{error}</div>:null}

    <section className="soldout-controls glass-panel">
      <input aria-label="搜尋供應節點" value={query} onChange={event=>setQuery(event.target.value)} placeholder="搜尋產品／原料／節點" />
      <div className="soldout-filters" aria-label="狀態篩選">
        {(['all','available','soldout','paused'] as const).map(status=><button key={status} aria-pressed={statusFilter===status} onClick={()=>setStatusFilter(status)}>{status==='all'?'全部':statusLabel[status]}</button>)}
      </div>
      <button onClick={selectVisible} disabled={!visibleNodes.length}>全選目前結果</button>
      <button onClick={clearSelection} disabled={!selected.length}>清除選擇</button>
    </section>

    <section className="soldout-list" aria-label="供應節點">
      {visibleNodes.map(node=><article className={`soldout-node status-${node.status} glass-panel`} key={node.nodeId}>
        <label className="soldout-select"><input type="checkbox" aria-label={`選擇 ${node.nodeId}`} checked={selectedSet.has(node.nodeId)} onChange={()=>toggle(node.nodeId)} /><span/></label>
        <div className="soldout-node-body"><div className="soldout-node-title"><b>{node.nodeId}</b><span>{kindLabel[node.nodeKind]}</span></div>{node.productId?<small>Product {node.productId}</small>:null}<small>影響 {node.affectedProductIds.length} 項產品</small>{node.reasonNote?<small>{node.reasonNote}</small>:null}</div>
        <div className="soldout-node-status"><strong>{statusLabel[node.status]}</strong><small>{node.source}{node.reasonCode?` · ${node.reasonCode}`:''}</small></div>
      </article>)}
      {!visibleNodes.length?<div className="soldout-no-result glass-panel">目前篩選冇結果。</div>:null}
    </section>

    {snapshot.schedules.length?<section className="soldout-schedules glass-panel"><div><span className="eyebrow">RESTORE SCHEDULES</span><h2>已安排恢復</h2></div>{snapshot.schedules.map(schedule=><article key={schedule.scheduleId}><b>{schedule.scheduleId}</b><span>{schedule.restoreAt}</span><small>{schedule.status} · {schedule.nodeIds.length} 個節點</small></article>)}</section>:null}

    <section className="soldout-actions glass-panel" aria-label="供應操作"><span>{selected.length} 個已選</span><button disabled={!canMutate} onClick={()=>void runIntent('availability.mark-soldout')}>售罄</button><button disabled={!canMutate} onClick={()=>void runIntent('availability.restore')}>恢復</button><button disabled={!canMutate} onClick={()=>void runIntent('availability.pause')}>暫停</button><button disabled={!canMutate} onClick={()=>void runIntent('availability.resume')}>恢復出品</button></section>
  </main>;
}
